import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemText,
  Button,
  ListItemButton,
  ListItemIcon,
  Typography,
  Chip,
} from "@mui/material";
import {
  AttachFile,
  Edit,
  MailOutline,
  Person,
  Assignment,
} from "@mui/icons-material";
import { APIEmail, getAllEmails, getEmail } from "../api/emails";
import { getAllCustomers } from "../api/customers";
import { getAllProposals } from "../api/proposals";
import emailStyle from "./Email.module.css";
import SplitPageView from "../components/SplitPageView";
import EmailDetailsPage from "./EmailEditPage";

function formatWhen(ms?: number) {
  if (!ms) return "";
  const d = new Date(ms);
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function EmailListPage() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [emailData, setEmailData] = useState<APIEmail[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<APIEmail | undefined>();
  const [search, setSearch] = useState("");
  // Customer name for each customer id, and each proposal id.
  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    getAllEmails().then((v) =>
      setEmailData(
        [...v].sort((a, b) => (b.generatedAt || 0) - (a.generatedAt || 0))
      )
    );
  }, [id]);

  useEffect(() => {
    Promise.all([getAllCustomers(), getAllProposals()]).then(
      ([customers, proposals]) => {
        const n: Record<string, string> = {};
        customers.forEach(
          (c) => (n[c.id!] = `${c.firstName} ${c.lastName}`.trim())
        );
        proposals.forEach(
          (p) => (n[p.id!] = `${p.firstName} ${p.lastName}`.trim())
        );
        setNames(n);
      }
    );
  }, []);

  useEffect(() => {
    if (!id) {
      setSelectedEmail(undefined);
      return;
    }
    getEmail(id).then((v) => setSelectedEmail(v));
  }, [id]);

  const visible = useMemo(() => {
    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
    return emailData.filter((e) => {
      const haystack = [e.subject, e.to, names[e.refId || ""]]
        .join(" ")
        .toLowerCase();
      return terms.every((t) => haystack.includes(t));
    });
  }, [emailData, search, names]);

  const statusChip = (e: APIEmail) =>
    e.sentAt ? (
      <Chip size="small" color="success" variant="outlined" label="Sent" />
    ) : (
      <Chip size="small" color="warning" variant="outlined" label="Pending" />
    );

  return (
    <SplitPageView
      selected={!!id || !!searchParams.get("new")}
      pageTitle="Emails"
      search={search}
      onSearchChange={setSearch}
      contentList={
        <List style={{ padding: 0 }}>
          <ListItem>
            <Button
              component={Link}
              to="/emails?new=1"
              variant="contained"
              startIcon={<Edit />}
              fullWidth
            >
              New Email
            </Button>
          </ListItem>
          {visible.map((email) => (
            <ListItemButton
              component={Link}
              to={`/emails?id=${email.id}`}
              key={email.id}
            >
              <ListItemIcon>
                <MailOutline color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={email.subject || "(no subject)"}
                secondary={[names[email.refId || ""], email.to]
                  .filter(Boolean)
                  .join(" · ")}
                primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
              />
              <div className={emailStyle.rowMeta}>
                <Typography variant="caption" color="text.secondary">
                  {formatWhen(email.generatedAt)}
                </Typography>
                {statusChip(email)}
              </div>
            </ListItemButton>
          ))}
          {visible.length === 0 && (
            <ListItem>
              <ListItemText
                secondary={search ? "No emails match." : "No emails yet."}
              />
            </ListItem>
          )}
        </List>
      }
      mainContent={
        selectedEmail ? (
          <article className={emailStyle.message}>
            <Typography variant="h5">
              {selectedEmail.subject || "(no subject)"}
            </Typography>
            <dl className={emailStyle.meta}>
              <dt>To</dt>
              <dd>{selectedEmail.to}</dd>
              <dt>From</dt>
              <dd>{selectedEmail.from}</dd>
              <dt>Date</dt>
              <dd>
                {selectedEmail.generatedAt
                  ? new Date(selectedEmail.generatedAt).toLocaleString()
                  : ""}
              </dd>
              <dt>Status</dt>
              <dd>
                {statusChip(selectedEmail)}
                {selectedEmail.sentAt && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    {new Date(selectedEmail.sentAt).toLocaleString()}
                  </Typography>
                )}
              </dd>
              {selectedEmail.refId && (
                <>
                  <dt>About</dt>
                  <dd>
                    {selectedEmail.refType === "customer" ? (
                      <Chip
                        icon={<Person />}
                        label={names[selectedEmail.refId] || "Customer"}
                        component={Link}
                        to={`/customers?id=${selectedEmail.refId}`}
                        clickable
                      />
                    ) : (
                      <Chip
                        icon={<Assignment />}
                        label={`Proposal · ${
                          names[selectedEmail.refId] || ""
                        }`}
                        component={Link}
                        to={`/proposals?id=${selectedEmail.refId}`}
                        clickable
                      />
                    )}
                  </dd>
                </>
              )}
              {selectedEmail.attachments && (
                <>
                  <dt>Attached</dt>
                  <dd>
                    <Chip
                      size="small"
                      icon={<AttachFile />}
                      label="Proposal PDF"
                    />
                  </dd>
                </>
              )}
            </dl>
            <div className={emailStyle.body}>{selectedEmail.body}</div>
          </article>
        ) : (
          <EmailDetailsPage />
        )
      }
    />
  );
}

export default EmailListPage;
